/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
import React, { type ReactNode } from 'react';

import { styled } from '@compiled/react';

import { reduceMotionAsPerUserPreference, useIsReducedMotion } from '@atlaskit/motion';
import { Box, xcss, type XCSS } from '@atlaskit/primitives';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GradientPropertyContainer = styled.div({
	width: '100%',
	position: 'relative',
	'@property --gradient-angle': {
		syntax: `"<angle>"`,
		initialValue: '270deg',
		inherits: 'false',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'@keyframes rotationAnimation': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'0%': {
			'--gradient-angle': '0deg',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'100%': {
			'--gradient-angle': '360deg',
		},
	},
	maxHeight: 'inherit',
});

const outerContainerStyles = xcss({
	overflow: 'hidden',
	position: 'relative',
	background: `conic-gradient(
    from var(--gradient-angle, 45deg),
    #0065FF 10%,
    #0469FF 30%,
    #BF63F3 50%,
    #FFA900 70%,
    #0065FF 90%
  )`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...reduceMotionAsPerUserPreference,
	maxHeight: 'inherit',
});

const animationStyles = xcss({
	animation: 'rotationAnimation linear 2s infinite',
});

const baseGlowStyles = xcss({
	position: 'absolute',
	filter: 'blur(25px)',
	opacity: '0.5',
	height: '100%',
	width: '100%',
});

const defaultBorderRadiusStyles = xcss({
	borderRadius: 'border.radius.200',
});

const modalBorderRadiusStyles = xcss({
	borderRadius: 'border.radius.100',
});

const innerContainerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	maxHeight: 'inherit',
	display: 'flex',
	flexDirection: 'column',
	backgroundClip: 'content-box',
});

export type AIBorderProps = {
	loading?: boolean;
	children?: ReactNode;
	useModalStyles?: boolean;
	hideGlow?: boolean;
	customInnerContainerStyles?: XCSS;
	customGlowStyles?: XCSS;
};

export const AIBorder = ({
	loading = false,
	children,
	useModalStyles,
	hideGlow,
	customInnerContainerStyles,
	customGlowStyles,
	...props
}: AIBorderProps) => {
	const isReducedMotion = useIsReducedMotion();
	const shouldRotate = loading && !isReducedMotion;
	const containerStyles = shouldRotate
		? [outerContainerStyles, animationStyles]
		: [outerContainerStyles];
	const glowStyles = customGlowStyles ? [baseGlowStyles, customGlowStyles] : [baseGlowStyles];

	const borderRadiusStyles = useModalStyles ? modalBorderRadiusStyles : defaultBorderRadiusStyles;

	return (
		<GradientPropertyContainer data-testid="ai-border-container" {...props}>
			{shouldRotate && (
				<Box
					xcss={[...containerStyles, borderRadiusStyles, ...(hideGlow ? [] : glowStyles)]}
					testId="loading-glow"
				/>
			)}
			<Box xcss={[...containerStyles, borderRadiusStyles]}>
				<Box
					padding="space.025"
					xcss={[innerContainerStyles, borderRadiusStyles, customInnerContainerStyles]}
				>
					{children}
				</Box>
			</Box>
		</GradientPropertyContainer>
	);
};
